import { LINKEDIN_PUBLISH_AS } from "../../../../utils/constants";

const extractLinkedInOrgIdFromReference = (reference = "") => {
  if (!reference) return "";
  const match = reference.match(/urn:li:organization:(.+)/);
  return match ? match[1] : "";
};

export const createInstagramAccountOption = (account) => {
  if (!account) return null;
  return {
    id: account.integrationID || account.id || account.instagramID || "",
    name: account.name || account.instagramID || account.integrationID || "",
    integrationID: account.integrationID || "",
    instagramID: account.instagramID || "",
    pageID: account.pageID || "",
  };
};

export const findInstagramAccountOption = (
  platformData,
  businessAccounts = [],
  { requireActiveMatch = false } = {}
) => {
  if (!platformData) return null;

  const matchedAccount =
    businessAccounts.find(
      (account) =>
        platformData.instagramID &&
        account.instagramID === platformData.instagramID
    ) ||
    businessAccounts.find(
      (account) =>
        platformData.integrationID &&
        account.integrationID === platformData.integrationID
    );

  if (matchedAccount) {
    return createInstagramAccountOption(matchedAccount);
  }

  if (requireActiveMatch) {
    return null;
  }

  if (platformData.instagramID || platformData.integrationID) {
    return {
      id: platformData.integrationID || platformData.instagramID,
      name:
        platformData.instagramID ||
        platformData.integrationID ||
        "Instagram Account",
      integrationID: platformData.integrationID || "",
      instagramID: platformData.instagramID || "",
      pageID: platformData.pageID || "",
    };
  }

  return null;
};

export const findFacebookAccountOption = (
  platformData,
  facebookPages = [],
  { requireActiveMatch = false } = {}
) => {
  if (!platformData) return null;

  const matchedPage = facebookPages.find(
    (page) => platformData.pageID && page.id === platformData.pageID
  );

  if (matchedPage) {
    return {
      id: matchedPage.id,
      name: matchedPage.name || matchedPage.id,
      integrationID: matchedPage.integrationID || "",
      imageURL: matchedPage.imageURL || "",
    };
  }

  if (requireActiveMatch) {
    return null;
  }

  if (platformData.pageID || platformData.integrationID) {
    return {
      id: platformData.pageID || platformData.integrationID,
      name: platformData.pageName || platformData.pageID || "Facebook Page",
      integrationID: platformData.integrationID || "",
      imageURL: platformData.imageURL || "",
    };
  }

  return null;
};

export const findTikTokAccountOption = (
  platformData,
  integrations = {},
  { requireActiveMatch = false } = {}
) => {
  if (!platformData?.integrationID) return null;
  const integrationID = platformData.integrationID;
  const tikTokIntegration = integrations?.tiktok?.[integrationID];

  if (requireActiveMatch && !tikTokIntegration) {
    return null;
  }

  return {
    id: integrationID,
    name: tikTokIntegration?.name || platformData.accountName || integrationID,
    integrationID,
  };
};

export const findYouTubeAccountOption = (
  platformData,
  integrations = {},
  { requireActiveMatch = false } = {}
) => {
  const integrationID = platformData?.integrationID || platformData?.channelID;
  if (!integrationID) return null;

  const youtubeIntegration = integrations?.youtube?.[integrationID];

  if (requireActiveMatch && !youtubeIntegration) {
    return null;
  }

  const channelID =
    youtubeIntegration?.channelID || platformData?.channelID || "";

  return {
    id: integrationID,
    name:
      youtubeIntegration?.channelTitle ||
      youtubeIntegration?.channelID ||
      platformData?.channelTitle ||
      integrationID,
    integrationID,
    channelID,
  };
};

const buildLinkedInOrganizationOptionFromPage = ({ integration, page }) => {
  const orgId = String(page.id);
  const organizationUrn =
    page.organizationUrn || `urn:li:organization:${orgId}`;
  const orgUrn = extractLinkedInOrgIdFromReference(organizationUrn);
  const matchingAdvertisers = Object.values(
    integration?.advertisers || {}
  ).filter(
    (advertiser) =>
      advertiser?.reference === organizationUrn ||
      extractLinkedInOrgIdFromReference(advertiser?.reference) === orgId
  );

  return {
    id: orgId,
    name: page.name || orgId,
    integrationID: integration.id,
    publishAs: LINKEDIN_PUBLISH_AS.ORGANIZATION,
    orgUrn,
    organizationUrn,
    displayLabel: page.name || orgId,
    isPage: true,
    pageId: orgId,
    advertiserIds: matchingAdvertisers.map((advertiser) => advertiser.id),
  };
};

const buildLinkedInProfileOption = (integration) => ({
  id: integration.id,
  name: integration?.name,
  integrationID: integration.id,
  publishAs: LINKEDIN_PUBLISH_AS.PROFILE,
  isPersonal: true,
  displayLabel: integration?.name || "",
});

export const findLinkedInAccountOption = (
  platformData,
  integrations = {},
  { requireActiveMatch = false } = {}
) => {
  const integrationID = platformData?.integrationID;
  if (!integrationID) return null;

  const integration = integrations?.linkedIn?.[integrationID];
  if (!integration) {
    return null;
  }

  const adAccountID = platformData?.adAccountID
    ? String(platformData.adAccountID)
    : "";

  if (adAccountID) {
    const pages = Object.values(integration.pages || {});
    for (const page of pages) {
      const option = buildLinkedInOrganizationOptionFromPage({
        integration,
        page,
      });
      const matchesAdvertiser = (option.advertiserIds || []).some(
        (id) => String(id) === adAccountID
      );
      const matchesOrgId = String(option.id) === adAccountID;
      if (matchesAdvertiser || matchesOrgId) {
        return option;
      }
    }

    const advertisers = Object.values(integration.advertisers || {});
    const matchedAdvertiser = advertisers.find(
      (advertiser) => String(advertiser.id) === adAccountID
    );
    if (matchedAdvertiser) {
      const orgUrn = extractLinkedInOrgIdFromReference(
        matchedAdvertiser.reference || ""
      );
      const pageName = matchedAdvertiser.pageName || "";
      return {
        id: matchedAdvertiser.id,
        name: pageName || orgUrn || matchedAdvertiser.id,
        integrationID: integration.id,
        publishAs: LINKEDIN_PUBLISH_AS.ORGANIZATION,
        orgUrn,
        displayLabel: pageName || orgUrn || matchedAdvertiser.id,
        advertiserIds: [matchedAdvertiser.id],
      };
    }

    if (requireActiveMatch) {
      return null;
    }
  }

  return buildLinkedInProfileOption(integration);
};
